import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { putMyAssumptions } from "@penn/web-common";
import { useCloseOnNavigate } from "@/hooks/useCloseOnNavigate";
import { PennButton, PennInput, PennText } from "@/components/ui";
import { getApiErrorMessage } from "@penn/web-common";
import { dispatchCompareCostInvalidate } from "@penn/web-common";
import {
  assumptionsDtoToForm,
  formToAssumptionsDto,
  type AssumptionsFormValues,
} from "@/utils/assumptions-mapper";

// C5 (POC parity): every field is optional — a blank value is allowed and means
// "use the backend global default". A provided value is still validated.
const optionalPositive = z.union([
  z.literal(""),
  z.coerce.number({ invalid_type_error: "Required" }).positive(),
]);
const optionalNonNeg = z.union([
  z.literal(""),
  z.coerce.number({ invalid_type_error: "Required" }).nonnegative(),
]);

// Blank means "use the backend global default"; a provided value must be > 0
// and within a realistic upper bound (a year has at most 365 days, a day at
// most 24 hours).
const optionalPositiveMax = (max: number) =>
  z.union([
    z.literal(""),
    z.coerce
      .number({ invalid_type_error: "Required" })
      .positive()
      .max(max, `Must be ${max} or fewer`),
  ]);

const assumptionsSchema = z.object({
  laborRate: optionalPositive,
  workingDaysPerYear: optionalPositiveMax(365),
  workingHoursPerDay: optionalPositiveMax(24),
  usefulLifeMachines: optionalPositive,
  scrapManual: optionalNonNeg,
  scrapAutomated: optionalNonNeg,
  reworkCost: optionalNonNeg,
  oeeManual: optionalNonNeg,
  oeeAutomated: optionalNonNeg,
});

type CustomizeAssumptionsProps = {
  onClose: () => void;
  onSaved: (form: AssumptionsFormValues) => void;
  initialValues: AssumptionsFormValues;
  readOnly?: boolean;
  readOnlyMessage?: string;
};

export const CustomizeAssumptions = ({
  onClose,
  onSaved,
  initialValues,
  readOnly = false,
  readOnlyMessage,
}: CustomizeAssumptionsProps) => {
  // Custom backdrop (not PennModal): wire Back-to-close explicitly so the modal
  // can't leave its backdrop stuck after a browser Back navigation.
  useCloseOnNavigate(true, onClose);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AssumptionsFormValues>({
    resolver: zodResolver(assumptionsSchema),
    mode: "onChange",
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const onSubmit = async (data: AssumptionsFormValues) => {
    if (readOnly) {
      onClose();
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await putMyAssumptions(formToAssumptionsDto(data));
      onSaved(assumptionsDtoToForm(saved));
      dispatchCompareCostInvalidate();
      onClose();
    } catch (e) {
      setSaveError(getApiErrorMessage(e, "Failed to save assumptions"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white rounded-2xl shadow-soft overflow-hidden">
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <PennText as="h2" size="lg" color="slate-900" font="semibold" className="mt-0.5">
                Customize assumptions
              </PennText>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <PennText as="p" size="sm" color="slate-700" className="mt-2">
            {readOnly
              ? readOnlyMessage ??
                "Global defaults apply for an unregistered Work-For customer. Saving is not available."
              : "These values are stored in your user account and will be reused next time you sign in."}
          </PennText>
        </div>

        <fieldset disabled={readOnly || saving} className="disabled:opacity-60">
          <div className="px-6 pb-4 grid grid-cols-2 gap-4">
            <FieldCard label="Labor rate (currency/hour)" error={errors.laborRate?.message}>
              <PennInput type="number" step="any" placeholder="e.g., 45" error={!!errors.laborRate} {...register("laborRate")} />
            </FieldCard>
            <FieldCard label="Working days per year" error={errors.workingDaysPerYear?.message}>
              <PennInput type="number" step="any" placeholder="e.g., 220" error={!!errors.workingDaysPerYear} {...register("workingDaysPerYear")} />
            </FieldCard>
            <FieldCard label="Working hours per day" error={errors.workingHoursPerDay?.message}>
              <PennInput type="number" step="any" placeholder="e.g., 8" error={!!errors.workingHoursPerDay} {...register("workingHoursPerDay")} />
            </FieldCard>
            <FieldCard label="Useful life of machines (years)" error={errors.usefulLifeMachines?.message}>
              <PennInput type="number" step="any" placeholder="e.g., 7" error={!!errors.usefulLifeMachines} {...register("usefulLifeMachines")} />
            </FieldCard>
            <FieldCard label="Scrap / rework factor">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <PennText as="span" size="xs" color="slate-600" font="semibold" className="mb-1 block">Manual</PennText>
                  <PennInput type="number" step="any" placeholder="e.g., 0.01" {...register("scrapManual")} />
                </div>
                <div>
                  <PennText as="span" size="xs" color="slate-600" font="semibold" className="mb-1 block">Automated</PennText>
                  <PennInput type="number" step="any" placeholder="e.g., 0.0001" {...register("scrapAutomated")} />
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100">
                <PennText as="span" size="xs" color="slate-600" font="semibold" className="mb-1 block">Rework cost (per defect)</PennText>
                <PennInput type="number" step="any" placeholder="e.g., 50" {...register("reworkCost")} />
              </div>
            </FieldCard>
            <FieldCard label="OEE (%)">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <PennText as="span" size="xs" color="slate-600" font="semibold" className="mb-1 block">Manual</PennText>
                  <PennInput type="number" step="any" placeholder="e.g., 70" {...register("oeeManual")} />
                </div>
                <div>
                  <PennText as="span" size="xs" color="slate-600" font="semibold" className="mb-1 block">Automated</PennText>
                  <PennInput type="number" step="any" placeholder="e.g., 85" {...register("oeeAutomated")} />
                </div>
              </div>
            </FieldCard>
          </div>
        </fieldset>

        {saveError && (
          <PennText as="p" size="sm" color="error" className="px-6 pb-2">
            {saveError}
          </PennText>
        )}

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <PennText as="p" size="xs" color="slate-500">
            {readOnly ? "Close to continue." : "Saved values will be loaded automatically for your account."}
          </PennText>
          <div className="flex items-center gap-3">
            <PennButton type="button" variant="secondary" size="md" onClick={onClose}>
              Close
            </PennButton>
            {!readOnly && (
              <PennButton type="submit" variant="primary" size="md" disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </PennButton>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};

const FieldCard = ({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-gray-200 px-4 py-3">
    <PennText as="p" size="sm" color="slate-800" font="semibold" className="mb-3">
      {label}
    </PennText>
    {children}
    {error && (
      <PennText as="p" size="xs" color="error" className="mt-1.5">
        {error}
      </PennText>
    )}
  </div>
);
